/**
 * TradesExplorer — winners/losers on demand. Best / Worst / Recent tabs,
 * sortable, and honours the active breakdown filter (drill-down).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, Tabs, Tab, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useTabs } from '../../contexts/TabsContext';
import { analyticsService, AnalyticsScope, ExplorerTradeRow } from '../../services/analytics.service';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  days: number;
  scope: AnalyticsScope;
  filter: { dim: string; value: string; label: string } | null;
  onClearFilter: () => void;
}

const TAB_SORT = ['worst', 'best', 'recent'] as const;
type TabId = typeof TAB_SORT[number];
const sortFor = (t: TabId) => (t === 'best' ? 'pnl_desc' : t === 'worst' ? 'pnl_asc' : 'date_desc');

const fmtHold = (h: number | null) => h == null ? '—' : h < 1 ? `${Math.round(h * 60)}m` : h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`;
const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const TradesExplorer: React.FC<Props> = ({ days, scope, filter, onClearFilter }) => {
  const { C } = useAppTheme();
  const { openChartTab } = useTabs();
  const [tab, setTab] = useState<TabId>('worst');
  const [rows, setRows] = useState<ExplorerTradeRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await analyticsService.getTrades({
        days, scope, sort: sortFor(tab), limit: 50,
        dim: filter?.dim, dimValue: filter?.value,
      });
      setRows(data);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [days, scope, tab, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card sx={{ borderRadius: 1.5 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography fontWeight={700}>Trades explorer</Typography>
            {filter && (
              <Chip size="small" onDelete={onClearFilter} deleteIcon={<ClearIcon />} label={filter.label}
                sx={{ fontWeight: 700, bgcolor: C.accentMuted, color: C.accent }} />
            )}
          </Box>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 34, mb: 1, '& .MuiTab-root': { minHeight: 34, py: 0, textTransform: 'none', fontWeight: 700 } }}>
          <Tab value="worst" label="Worst" />
          <Tab value="best" label="Best" />
          <Tab value="recent" label="Recent" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, fontSize: '0.85rem' }}>No trades in this slice.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Symbol', 'Side', 'Net P&L', 'R', 'ROI %', 'Hold', 'Exit', 'Str', 'Closed', ''].map((h, i) => (
                    <TableCell key={h + i} align={i >= 2 && i <= 4 ? 'right' : 'left'} sx={{ py: 0.5, fontWeight: 700, fontSize: '0.7rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: '0.78rem', fontFamily: 'ui-monospace, monospace' }}>{r.symbol}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip label={r.side} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: r.side === 'LONG' ? C.bullMuted : C.warnMuted, color: r.side === 'LONG' ? C.bull : C.warn }} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', fontWeight: 700, color: r.realizedPnl >= 0 ? C.bull : C.bear }}>{formatCurrency(r.realizedPnl)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', color: r.rMultiple >= 0 ? C.bull : C.bear }}>{r.rMultiple >= 0 ? '+' : ''}{r.rMultiple.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', color: r.roiPct >= 0 ? C.bull : C.bear }}>{r.roiPct >= 0 ? '+' : ''}{r.roiPct.toFixed(2)}%</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>{fmtHold(r.holdHours)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.72rem', color: 'text.secondary' }}>{r.exitCategory}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.68rem', color: 'text.secondary' }}>{r.entryStrength || '—'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.72rem', color: 'text.secondary' }}>{fmtDate(r.closedAt)}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Tooltip arrow title={`Open ${r.symbol} chart`}>
                        <IconButton size="small" sx={{ p: 0.2 }} onClick={() => openChartTab(r.symbol, '')}>
                          <CandlestickChartIcon sx={{ fontSize: 15, color: C.neutral }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TradesExplorer;
